import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { CircleNotch, TrendDown, TrendUp, WarningCircle } from '@phosphor-icons/react';
import { formatNumber, toFaDigits } from '@aramis/shared';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md';
const variants: Record<Variant, string> = {
  primary: 'bg-inverse text-on-inverse hover:opacity-90',
  accent: 'bg-accent text-on-accent hover:bg-accent-pressed',
  secondary: 'bg-surface text-text border border-control hover:bg-sunken',
  ghost: 'text-text hover:bg-sunken',
  danger: 'bg-surface text-danger border border-danger/40 hover:bg-danger-soft',
};
const sizes: Record<Size, string> = { sm: 'h-8 px-3 text-[13px] gap-1.5', md: 'h-10 px-4 text-sm gap-2' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> { variant?: Variant; size?: Size; loading?: boolean; icon?: ReactNode }
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest }, ref) {
  return (
    <button ref={ref} disabled={disabled || loading} {...rest}
      className={cx('pressable inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-control font-medium disabled:opacity-45', variants[variant], sizes[size], className)}>
      {loading ? <CircleNotch className="animate-spin" size={16} aria-hidden /> : icon}
      {children && <span>{children}</span>}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; size?: 'sm' | 'md' }>(
  function IconButton({ label, size = 'md', className, children, ...rest }, ref) {
    return (
      <button ref={ref} aria-label={label} title={label} {...rest}
        className={cx('pressable inline-grid shrink-0 place-items-center rounded-control text-muted hover:bg-sunken hover:text-text disabled:opacity-40', size === 'sm' ? 'size-8' : 'size-10', className)}>
        {children}
      </button>
    );
  });

type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
const tones: Record<Tone, string> = {
  neutral: 'bg-sunken text-muted', accent: 'bg-accent-soft text-accent', success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning', danger: 'bg-danger-soft text-danger',
};
export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx('inline-flex h-6 items-center whitespace-nowrap rounded-control px-2 text-xs font-medium transition-colors duration-(--m-dur-micro)', tones[tone], className)}>{children}</span>;
}

export const ORDER_TONE: Record<string, Tone> = {
  pending_payment: 'warning', awaiting_confirmation: 'accent', confirmed: 'accent', preparing: 'accent', ready_for_pickup: 'accent',
  shipped: 'neutral', out_for_delivery: 'neutral', delivered: 'success', picked_up: 'success', canceled: 'danger', payment_failed: 'danger', returned: 'neutral',
};

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx('skeleton rounded-control', className)} />;
}

/** Empty states teach the next step instead of saying "nothing here". */
export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon && <div className="mb-4 text-subtle">{icon}</div>}
      <p className="text-base font-medium">{title}</p>
      {body && <p className="mt-1 max-w-[46ch] text-muted">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <WarningCircle size={28} className="text-danger" aria-hidden />
      <p className="max-w-[46ch] text-muted">{message ?? 'اطلاعات بارگذاری نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'}</p>
      {onRetry && <Button size="sm" onClick={onRetry}>تلاش دوباره</Button>}
    </div>
  );
}

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {back}
        <h1 className="text-[1.375rem] font-medium leading-tight">{title}</h1>
        {description && <p className="mt-1 text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** A titled block separated by space and a hairline, not a floating card. */
export function Section({ title, description, actions, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('border-t border-line pt-5 pb-7', className)}>
      {(title || actions) && (
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            {title && <h2 className="text-[0.9375rem] font-medium">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-muted">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('rounded-control border border-line bg-surface', className)}>{children}</div>;
}

export function Money({ value, className, strike }: { value: number | null | undefined; className?: string; strike?: boolean }) {
  if (value == null) return <span className="text-subtle">-</span>;
  return <span className={cx('num whitespace-nowrap', strike && 'text-subtle line-through', className)}>{formatNumber(value)} <span className="text-[0.85em] text-muted">تومان</span></span>;
}

export const fa = (n: number | string) => toFaDigits(n);

/** Numbers arrive, they don't appear (MASTER 8.2): counts from the last shown value; instant under reduced motion. */
function useCountUp(value: number, ms = 900) {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { from.current = value; setShown(value); return; }
    const a = from.current, start = performance.now();
    let raf = requestAnimationFrame(function tick(t) {
      const p = Math.min(1, (t - start) / ms), v = Math.round(a + (value - a) * (1 - (1 - p) ** 4));
      from.current = v;
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}
export function Count({ value, money }: { value: number; money?: boolean }) {
  const v = useCountUp(value);
  return money ? <Money value={v} /> : <>{fa(v)}</>;
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode; count?: number }[] }) {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-line">
      {items.map((i) => {
        const active = i.value === value;
        return (
          <button key={i.value} role="tab" aria-selected={active} onClick={() => onChange(i.value)}
            className={cx('relative -mb-px flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-sm transition-colors', active ? 'border-accent font-medium text-text' : 'border-transparent text-muted hover:text-text')}>
            {i.label}
            {i.count != null && i.count > 0 && <span className={cx('num rounded-control px-1.5 text-xs', active ? 'bg-accent-soft text-accent' : 'bg-sunken text-muted')}>{fa(i.count)}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Stat({ label, value, delta, hint }: { label: string; value: ReactNode; delta?: number | null; hint?: ReactNode }) {
  return (
    <div className="min-w-0 py-1">
      <div className="text-[13px] text-muted">{label}</div>
      <div className="num mt-1 truncate text-xl font-medium leading-tight">{value}</div>
      <div className="mt-1 flex items-center gap-2 text-xs">
        {delta != null && Number.isFinite(delta) && (
          <span className={cx('num inline-flex items-center gap-0.5', delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-subtle')}>
            {delta > 0 ? <TrendUp size={13} weight="bold" aria-label="افزایش" /> : delta < 0 ? <TrendDown size={13} weight="bold" aria-label="کاهش" /> : null}
            ٪{fa(Math.abs(Math.round(delta)))}
          </span>
        )}
        {hint && <span className="text-subtle">{hint}</span>}
      </div>
    </div>
  );
}
